import { useState } from 'react'
import { toast } from 'sonner'
import { History, Loader2, Trash2 } from 'lucide-react'
import {
  useClearDocumentWorkspaceContext,
  useDocumentWorkspaceContext,
  useDocumentWorkspaceContextAudit,
  useSaveDocumentWorkspaceContext,
} from '@/api/queries'
import {
  MAX_CONTEXT_CHARS,
  type DocumentDetail,
} from '@/api/docfiller-types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ContextAuditTimeline } from '@/components/docfiller/ContextAuditTimeline'
import { SavedPromptsPicker } from '@/components/docfiller/SavedPromptsManager'

/**
 * Editor for the document-scope reusable context (``WorkspaceContext`` with
 * ``scope=document``). Distinct from ``DocumentContextPanel`` (which edits
 * the upload-time snapshot ``documents.initial_context``):
 *
 *   1. snapshot         → captured at upload, edited here-but-separately
 *   2. document-scope   → reusable per-doc context (THIS panel)
 *   3. user-scope       → workspace default (``WorkspaceContextDialog``)
 *
 * The split lets the user keep a per-doc reusable context that survives
 * snapshot edits — useful when the snapshot is a frozen point-in-time copy
 * of a now-stale workspace default but the user wants to keep iterating
 * on the per-doc context without forgetting where they left off.
 *
 * Wires:
 *  - Token-budget bar against ``MAX_CONTEXT_CHARS``.
 *  - Optimistic save / clear with rollback on error.
 *  - Destructive confirmation step on Clear.
 *  - Audit timeline tab fed by ``GET /documents/{uuid}/workspace-context/audit``.
 *  - ``readOnly`` mode for users without write access.
 */
export function DocumentReusableContextPanel({
  doc,
  readOnly = false,
}: {
  doc: DocumentDetail
  readOnly?: boolean
}) {
  const { data, isLoading } = useDocumentWorkspaceContext(doc.uuid)
  const save = useSaveDocumentWorkspaceContext(doc.uuid)
  const clear = useClearDocumentWorkspaceContext(doc.uuid)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [confirmingClear, setConfirmingClear] = useState(false)

  const current = data?.content ?? ''
  const hasSaved = current.length > 0
  const dirty = draft !== current
  const charCount = draft.length
  const tooLong = charCount > MAX_CONTEXT_CHARS
  const percent = Math.min(
    100,
    Math.round((charCount / MAX_CONTEXT_CHARS) * 100),
  )
  const tone: 'primary' | 'warning' | 'danger' = tooLong
    ? 'danger'
    : percent >= 80
      ? 'warning'
      : 'primary'

  const audit = useDocumentWorkspaceContextAudit(doc.uuid, { enabled: editing })

  const onStartEdit = () => {
    setDraft(current)
    setEditing(true)
    setConfirmingClear(false)
  }

  const onCancel = () => {
    setEditing(false)
    setConfirmingClear(false)
  }

  const onSave = async () => {
    if (readOnly) return
    const content = draft.trim()
    if (!content) {
      toast.error('Context cannot be empty')
      return
    }
    if (content.length > MAX_CONTEXT_CHARS) {
      toast.error(
        `Trim to ${MAX_CONTEXT_CHARS.toLocaleString()} characters before saving`,
      )
      return
    }
    try {
      await save.mutateAsync({ content })
      toast.success('Saved reusable document context')
      setEditing(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed')
    }
  }

  const onClearAsk = () => {
    if (readOnly || !hasSaved) return
    setConfirmingClear(true)
  }

  const onClearConfirm = async () => {
    setConfirmingClear(false)
    try {
      await clear.mutateAsync()
      toast.success('Cleared reusable document context')
      setEditing(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Clear failed')
    }
  }

  const onInsertPrompt = (body: string) => {
    setDraft((prev) => {
      const trimmed = prev.trimEnd()
      return trimmed ? `${trimmed}\n\n${body}` : body
    })
  }

  if (!editing) {
    return (
      <section
        data-testid="document-reusable-context-panel"
        data-readonly={readOnly || undefined}
        aria-labelledby="document-reusable-context-heading"
        className="space-y-2 rounded-lg border border-border bg-muted/40 p-3"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="space-y-0.5">
            <h3
              id="document-reusable-context-heading"
              className="aurora-eyebrow"
            >
              Reusable context (this document)
            </h3>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Composed alongside the snapshot — survives snapshot clears.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {readOnly && (
              <Badge
                tone="neutral"
                size="xs"
                data-testid="document-reusable-context-readonly"
              >
                Read-only
              </Badge>
            )}
            {hasSaved && (
              <Badge
                tone="primary"
                size="xs"
                data-testid="document-reusable-context-applied"
              >
                Applied
              </Badge>
            )}
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={onStartEdit}
                disabled={isLoading}
                data-testid="document-reusable-context-edit"
              >
                {isLoading && (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                )}
                {isLoading
                  ? 'Loading context...'
                  : hasSaved
                    ? 'Edit'
                    : 'Add reusable context'}
              </Button>
            )}
          </div>
        </div>
        {hasSaved ? (
          <pre
            data-testid="document-reusable-context-content"
            className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-border bg-card p-2 font-sans text-xs leading-relaxed text-foreground/90"
          >
            {current}
          </pre>
        ) : (
          <p className="text-xs leading-relaxed text-muted-foreground">
            No reusable context tied to this document yet. Anything saved
            here is composed alongside the snapshot at fill time and persists
            even if you clear the snapshot.
          </p>
        )}
      </section>
    )
  }

  return (
    <section
      data-testid="document-reusable-context-panel"
      aria-labelledby="document-reusable-context-heading"
      className="space-y-2 rounded-lg border border-border bg-muted/40 p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3
          id="document-reusable-context-heading"
          className="aurora-eyebrow"
        >
          Reusable context (this document)
        </h3>
        {hasSaved && !confirmingClear && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAsk}
            disabled={save.isPending || clear.isPending}
            data-testid="document-reusable-context-clear"
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="size-3.5" aria-hidden />
            Clear
          </Button>
        )}
      </div>

      <Tabs defaultValue="edit">
        <TabsList>
          <TabsTrigger
            value="edit"
            data-testid="document-reusable-context-tab-edit"
          >
            Edit
          </TabsTrigger>
          <TabsTrigger
            value="audit"
            data-testid="document-reusable-context-tab-audit"
          >
            <History className="size-3.5" /> Audit
          </TabsTrigger>
        </TabsList>

        <TabsContent value="edit" className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label
              htmlFor={`doc-reusable-context-${doc.uuid}`}
              className="sr-only"
            >
              Reusable context
            </Label>
            <SavedPromptsPicker onInsert={onInsertPrompt} />
          </div>
          <Textarea
            id={`doc-reusable-context-${doc.uuid}`}
            data-testid="document-reusable-context-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
            placeholder="Reusable context tied to this document only."
            className="resize-y text-xs"
            disabled={save.isPending || clear.isPending}
            aria-invalid={tooLong || undefined}
          />
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
              <span
                data-testid="document-reusable-context-charcount"
                className={tooLong ? 'text-destructive' : undefined}
              >
                {charCount.toLocaleString()} /{' '}
                {MAX_CONTEXT_CHARS.toLocaleString()}
              </span>
              {tooLong && (
                <Badge tone="danger" size="xs">
                  Too long
                </Badge>
              )}
            </div>
            <Progress
              percent={percent}
              size="sm"
              tone={tone}
              data-testid="document-reusable-context-budget-bar"
            />
          </div>

          {confirmingClear ? (
            <div
              className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive-soft/40 px-3 py-2 text-xs text-destructive-soft-foreground"
              role="alertdialog"
              aria-label="Confirm clear reusable document context"
              data-testid="document-reusable-context-clear-confirm"
            >
              <span className="font-medium">
                Clear the reusable context? This cannot be undone.
              </span>
              <div className="ml-auto flex gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmingClear(false)}
                  disabled={clear.isPending}
                  data-testid="document-reusable-context-clear-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={onClearConfirm}
                  disabled={clear.isPending}
                  data-testid="document-reusable-context-clear-confirm-button"
                >
                  {clear.isPending && (
                    <Loader2 className="size-3 animate-spin" />
                  )}
                  Clear
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onCancel}
                disabled={save.isPending}
                data-testid="document-reusable-context-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={onSave}
                disabled={
                  !dirty || tooLong || save.isPending || charCount === 0
                }
                data-testid="document-reusable-context-save"
              >
                {save.isPending && (
                  <Loader2 className="size-3.5 animate-spin" />
                )}
                Save
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="audit">
          <ContextAuditTimeline
            events={audit.data ?? []}
            isLoading={audit.isLoading}
            error={audit.error}
            testid="document-reusable-context-audit"
          />
        </TabsContent>
      </Tabs>
    </section>
  )
}
